import { DEFAULT_OPACITY } from "../../shared/constants";
import AbstractShape from "./AbstractShape";
import Coordinate from "../support/Coordinate";
import PolyCoordinate from "../support/PolyCoordinate";
import PolyCornerCoordinate from "../support/PolyCornerCoordinate";
import PolyVerticalBorder from "../support/PolyVerticalBorder";
import PolyHorizontalBorder from "../support/PolyHorizontalBorder";

export default class AbstractPolyShape extends AbstractShape {
    center;
    corners;
    hoveredSegment = null;

    constructor(provider, data, appearance, speciesIds, label) {
        super(provider, data, appearance, speciesIds, label);

        this.coordinates = data.coordinates.map((c, index) => new PolyCoordinate(this.provider, ...c, this, index));
        this.createSupportCoordinates();
        this.coordinates.map((c) => c.calculateRatio());
    }

    createSupportCoordinates() {
        let [minX, minY, maxX, maxY] = this.getMinMaxCoordinates();

        this.center = new Coordinate(this.provider,
            minX + (maxX - minX) / 2,
            minY + (maxY - minY) / 2
        );

        this.corners = [
            new PolyCornerCoordinate(this.provider, minX, minY, this), // top left
            new PolyCornerCoordinate(this.provider, maxX, maxY, this), // bottom right
            new PolyCornerCoordinate(this.provider, minX, maxY, this), // bottom left
            new PolyCornerCoordinate(this.provider, maxX, minY, this), // top right
        ];

        this.corners[0].setRelations(this.corners[2], this.corners[3], this.corners[1]);
        this.corners[1].setRelations(this.corners[3], this.corners[2], this.corners[0]);
        this.corners[2].setRelations(this.corners[0], this.corners[1], this.corners[3]);
        this.corners[3].setRelations(this.corners[1], this.corners[0], this.corners[2]);

        this.borders = [
            new PolyVerticalBorder(this.provider, this, this.corners[0], this.corners[2]),
            new PolyVerticalBorder(this.provider, this, this.corners[1], this.corners[3]),
            new PolyHorizontalBorder(this.provider, this, this.corners[0], this.corners[3]),
            new PolyHorizontalBorder(this.provider, this, this.corners[1], this.corners[2]),
        ];
    }

    updateSupportCoordinates() {
        let [minX, minY, maxX, maxY] = this.getMinMaxCoordinates();

        this.center.position(
            minX + (maxX - minX) / 2,
            minY + (maxY - minY) / 2
        );

        this.corners[0].position(minX, minY); // top left
        this.corners[1].position(maxX, maxY); // bottom right
        this.corners[2].position(minX, maxY); // bottom left
        this.corners[3].position(maxX, minY); // top right
    }

    display() {
        this.p.strokeJoin(this.p.ROUND);
        this.p.strokeCap(this.p.SQUARE);

        this.appearance.apply(this.isHovered, this.selected, this.showFlash, this.p.map(this.currentMaxSize, 500, 1500, DEFAULT_OPACITY, 50, true));
        this.p.beginShape();
        this.coordinates.map((c) => this.p.vertex(c.currentX, c.currentY));
        this.p.endShape(this.close);

        this.labels.display(this.currentMaxSize);

        if (this.editMode && this.selected && this.selection.length == 1) {
            this.borders.map((b) => b.display());
            this.coordinates.map((c) => c.display());
            this.corners.map((c) => c.display());
        }

        if (this.hoveredSegment !== null) {
            this.p.stroke(0, 50, 180);
            this.p.strokeWeight(3);
            this.p.line(
                this.hoveredSegment[0].currentX,
                this.hoveredSegment[0].currentY,
                this.hoveredSegment[1].currentX,
                this.hoveredSegment[1].currentY
            );
        }
    }

    mouseOver() {
        // not selected
        if (!this.selected) {
            if (this.hovered()) {
                this.cursor.pointer();
                return true;
            }
            return false;
        }

        // more elements selected
        if (this.selection.length > 1) {
            if (this.hovered()) {
                this.cursor.grab();
                return true;
            }
            return false;
        }

        // single element selected
        if (this.hoveredCoordinates()) {
            this.cursor.move();
            return true;
        }

        if (this.corners.find(c => c.mouseOver())) {
            return true;
        }

        if (this.borders.find(b => b.mouseOver())) {
            return true;
        }

        if (this.labels.mouseOver()) {
            return true;
        }

        if (this.hovered()) {
            this.cursor.grab();
            return true;
        }
        return false;
    }

    move(dx, dy) {
        this.coordinates.map((c) => c.move(dx, dy, false));
        this.corners.map((c) => c.move(dx, dy, false));
        this.center.move(dx, dy);
    }

    setPosition(x, y) {
        let dx = x - this.center.absX;
        let dy = y - this.center.absY;
        if (this.settings.snapToGrid) {
            let cx = this.coordinates[0].currentX;
            let cy = this.coordinates[0].currentY;
            let newX = Math.round((Math.round((cx + dx * this.view.scale) / this.grid.minorSize) * this.grid.minorSize) / this.view.scale)
            dx = newX - this.coordinates[0].absX;
            let newY = Math.round((Math.round((cy + dy * this.view.scale) / this.grid.minorSize) * this.grid.minorSize) / this.view.scale)
            dy = newY - this.coordinates[0].absY;
        }
        this.move(dx, dy);
        return [dx, dy];
    }

    hovered() {
        throw new Error('Not implemented');
    }

    hoveredCoordinates() {
        return this.coordinates.find((c) => c.hovered());
    }

    hoveredCorners() {
        return this.corners.find((c) => c.hovered());
    }

    hoveredBorders() {
        return this.borders.find((b) => b.hovered());
    }

    hoveredWithSupport() {
        return this.hoveredCoordinates()
            || this.hoveredCorners()
            || this.hoveredBorders()
            || (this.labels.hovered() ? this.labels : null)
            || (this.hovered() ? this : null);
    }

    surrounded(minX, minY, maxX, maxY) {
        return this.corners[0].absX >= minX
            && this.corners[0].absY >= minY
            && this.corners[1].absX <= maxX
            && this.corners[1].absY <= maxY;
    }

    segmentLength(index) {
        let [c1, c2] = this.getSegmentCoordinates(index);
        return c1.distanceTo(c2);
    }

    setSegmentLength(index, length) {
        if (length <= 0) {
            return false;
        }
        let [c1, c2] = this.getSegmentCoordinates(index);
        c2.setLengthFromCoordinate(length, c1);
        this.updateSupportCoordinates();
    }

    hoverSegment(index) {
        this.hoveredSegment = index !== null ? this.getSegmentCoordinates(index) : null;
    }

    getSegmentCoordinates(index) {
        throw new Error('Not implemented');
    }

    export() {
        throw new Error('Not implemented');
    }


    current(value) {
        return value * this.view.scale;
    }

    getMiniView(max) {
        let maxSize = this.absMaxSize;
        let dx = this.center.absX - maxSize / 2;
        let dy = this.center.absY - maxSize / 2;
        return this.coordinates.map((c) => {
            return [
                this.p.map(c.absX - dx, 0, maxSize, 0, max) + 2,
                this.p.map(c.absY - dy, 0, maxSize, 0, max) + 2,
            ];
        });
    }

    getMinMaxCoordinates() {
        return this.coordinates.reduce((acc, coordinate) => {
            acc[0] = acc[0] == null ? coordinate.absX : Math.min(acc[0], coordinate.absX);
            acc[1] = acc[1] == null ? coordinate.absY : Math.min(acc[1], coordinate.absY);
            acc[2] = acc[2] == null ? coordinate.absX : Math.max(acc[2], coordinate.absX);
            acc[3] = acc[3] == null ? coordinate.absY : Math.max(acc[3], coordinate.absY);
            return acc;
        }, [null, null, null, null]);
    }

    getClosestCoordinate() {
        let coordinate = this.coordinates[0];
        let distance = this.view.coordinate.absDistanceTo(coordinate);

        this.coordinates.map((c, index) => {
            if (index == 0) {
                return;
            }
            let d = this.view.coordinate.absDistanceTo(c);
            if (d < distance) {
                distance = d;
                coordinate = c;
            }
        });

        return coordinate;
    }

    get currentWidth() {
        return this.corners[1].currentX - this.corners[0].currentX;
    }

    get currentHeight() {
        return this.corners[1].currentY - this.corners[0].currentY;
    }

    get absWidth() {
        return this.corners[1].absX - this.corners[0].absX;
    }

    get absHeight() {
        return this.corners[1].absY - this.corners[0].absY;
    }

    get currentMaxSize() {
        return this.absMaxSize * this.view.scale;
    }

    get absMaxSize() {
        return Math.max(Math.abs(this.absWidth), Math.abs(this.absHeight));
    }

    get currentMinSize() {
        return this.absMinSize * this.view.scale;
    }

    get absMinSize() {
        return Math.min(Math.abs(this.absWidth), Math.abs(this.absHeight));
    }

    get absMinX() {
        return this.corners[0].absX;
    }

    get absMaxX() {
        return this.corners[1].absX;
    }

    get absMinY() {
        return this.corners[0].absY;
    }

    get absMaxY() {
        return this.corners[1].absY;
    }

    get length() {
        throw new Error('Not implemented');
    }
}
